import type { Messages } from '../messages.js'

/**
 * Russian. `ходы` for flips, because a `переворот` is four syllables and this is a meter.
 *
 * Four plural forms: one for 1, 21, 31, few for 2 to 4, many for 5 to 20 and for 0, other for
 * fractions. This is the locale that makes hand-written plural logic indefensible.
 */
export const ru: Messages = {
  tag: 'ru',

  readingDictionary: 'Чтение словаря…',
  noWordList: 'Нет списка слов для «{language}». Создайте его:  pnpm dictionary build',
  emptyWordList: 'Список слов для «{language}» пуст.',

  flips: 'ходы',
  score: 'очки',
  words: 'слова',
  round: 'раунд',
  ticksLeftLabel: 'Оставшееся время в раунде',
  typeAWord: 'наберите слово',
  tapPrompt: 'касание: взять или вернуть, затем {action}',

  boardOfTiles: 'Поле из {n} плиток',
  faceDown: 'закрыта',
  wildCard: 'джокер',
  wildKey: 'любая буква',
  letterReplaced: '{from} стала {to}',
  letterSwap: 'ЗАМЕНА БУКВЫ!',
  spentTile: 'использованная плитка',
  hiddenWhilePaused: 'скрыта на паузе',
  letterInWord: '{letter}, буква {position} в слове',

  completeWord: 'Принять слово',
  completeShort: 'Принять',
  reset: 'Сбросить',
  pause: 'Пауза',
  resume: 'Продолжить',
  newGame: 'Новая игра',
  paused: 'Пауза',
  outOfFlips: 'Ходы закончились',
  finalResult: '{score} очков, {words} за {rounds}',
  playAgain: 'Играть снова',
  share: 'Поделиться',
  shareCopied: 'Скопировано.',
  shareSelect: 'Скопируйте это:',

  lettersSelect: 'буквы выбирают',
  keysWild: 'берётся, когда вы набираете букву, которой нет ни на одной плитке',
  clearsEvery: 'снимает все выбранные {letter}',
  undoLastLetter: 'отменяет последнюю букву',
  noWordsYet: 'Слов пока нет.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'уже найдено',
  reasonTooShort: 'слишком короткое',
  reasonNotAWord: 'не слово',
  reasonAllFound: 'они у вас уже все есть',
  noSuchLetterUp: 'нет открытой {letter}',
  nothingUp: 'ничего не открыто',
  letterHidden: 'буква спряталась',
  shuffled: 'перемешано',
  shuffledAndBilled: 'перемешано, списано {flips} неиспользованных ходов',
  tooFewLetters: 'Осталось слишком мало букв',

  gameLanguage: 'язык',
  interfaceLanguage: 'интерфейс',
  dictionarySize: '{common} частых из {full} слов',
  filterLanguages: 'Поиск языков',
  noMatches: 'Ничего не найдено',

  nerdMode: 'подробный режим',
  rules: 'Правила',
  difficulty: 'сложность',
  difficultyNames: { easy: 'легко', medium: 'средне', hard: 'сложно', insane: 'жестоко' },
  pausedNoBoard: 'Эта игра не попадёт в таблицу лидеров.',
  about: 'Об игре',
  themeLabel: 'тема',
  themeNames: { traditional: 'традиционная', light: 'светлая', contrast: 'высокий контраст' },
  tiles: 'плитки (N)',
  secondsPerTick: 'секунд / такт',
  holdTicks: 'такты удержания',
  minWord: 'мин. слово',
  startingFlips: 'ходов на старте',
  wildChance: 'вероятность джокера',
  replaceChance: 'вероятность замены буквы',
  hideChance: 'вероятность скрытия буквы',
  wordCompleteMode: 'слово готово',
  wordCompleteNames: { shuffle: 'перемешать', spend: 'потратить', keep: 'оставить' },
  flipEconomy: 'экономика ходов',
  flipEconomyNames: {
    none: 'никакая',
    perLetter: 'по букве',
    fibonacci: 'фибоначчи',
    overMinimum: 'сверх минимума',
  },
  repeatedLetterKey: 'клавиша повторной буквы',
  keySchemeNames: { cycle: 'цикл', advance: 'вперёд' },
  keySchemeHelp: {
    cycle:
      'А берёт следующую свободную А, а когда все они в слове, снимает их. ' +
      'Shift+А тоже снимает.',
    advance: 'А берёт следующую свободную А. Shift+А снимает все А из слова.',
  },

  whatThatMeans: 'Что это значит',
  factRound: 'раунд',
  factWholeBoardUp: 'всё поле открыто',
  factRoundCosts: 'раунд стоит',
  factFlipsBuy: 'начальных ходов хватит на',
  factThisBoard: 'это поле',
  factBoardHadToAdmit: 'поле должно было допускать',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, самое длинное {longest}',
  wordsIncludingOneOf: '{words}, включая одно из {ceiling}',
  scorelessRounds: '{rounds} без очков',

  whatAWordPays: 'Что даёт слово',
  columnLetters: 'буквы',
  columnCost: 'цена',
  columnPoints: 'очки',
  columnFlips: 'ходы',
  columnNet: 'итог',

  canonicalRules: 'Стандартные правила: {difficulty}.',
  customRules: 'Изменено от предустановки. Результаты со своими правилами не ранжируются.',
  applyAndStart: 'Применить и начать игру',
  changesNextGame: 'Изменения вступят в силу со следующей игры.',
  presets: 'Предустановки:',

  start: 'Начать',
  restart: 'Заново',
  quit: 'Выйти',
  quitTitle: 'Выйти из этой игры?',
  restartTitle: 'Начать игру заново?',
  restartConfirm: 'Заново',
  quitConfirm: 'Выйти',
  keepPlaying: 'Продолжить',
  personalBest: 'Ваши лучшие игры',
  newPersonalBest: 'Новый личный рекорд.',
  columnRank: '№',
  notRanked: 'Свои правила, поэтому игра не ранжируется.',
  notRankedPaused: 'Часы остановились, поэтому игра не ранжируется.',
  rankOfTotal: '{rank} из {total}',

  howToPlay: 'Как играть',

  backToGame: 'Вернуться к игре',
  welcomeTitle: 'Добро пожаловать в Blinkered',
  tutorialSkip: 'Пропустить',
  tutorialNext: 'Далее',
  tutorialBack: 'Назад',
  tutorialStart: 'Начать игру',
  tutorialHideAgain: 'Больше не показывать',
  tutorialProgress: '{n} из {total}',
  tutorialSkipTitle: 'Пропустить знакомство?',
  tutGoalTitle: 'Blinkered — это гонка',
  tutGoalBody:
    'Вы начинаете игру с определённым числом ходов, и каждая открытая плитка стоит один ход. Когда ходы кончаются, игра заканчивается. Слова приносят ходы, и чем длиннее слово, тем больше ходов вы получаете.',
  tutSpentBody:
    'Буквы, которые вы использовали в слове, остаются закрытыми до следующего раунда. Только замена буквы меняет набор букв на поле.',
  tutChangeTitle: 'Буквы могут меняться',
  tutSameLetters:
    'Заметили слово, но не успели его сыграть? Те же буквы вернутся в следующем раунде, перемешанные по новым местам.',
  tutPickLetters: 'Нажимайте нужные буквы по порядку, чтобы составить слово.',
  tutMoreTurn:
    'Пока вы думаете, фишки продолжают открываться, так что может появиться буква получше.',
  tutTapBack:
    'Нажали не ту? Нажмите на неё ещё раз, чтобы вернуть. На любую, не только на последнюю.',
  tutComplete: 'Нажмите «Готово», когда слово собрано.',
  htBoardTitle: 'Поле',
  htBoardBody:
    'Плитки открываются по одной, в случайном порядке. Из открытых плиток вы составляете слова.',
  htWordsTitle: 'Слова',
  htWordsBody: 'Составьте слово из открытых плиток, набирая или щёлкая буквы по порядку.',
  htFlipsTitle: 'Ходы',
  htFlipsBody:
    'Каждая открытая плитка стоит один ход. Законченное слово возвращает ходы в ваш запас, а длинные слова возвращают больше. Когда ходы кончаются, игра заканчивается.',
  htRoundTitle: 'Раунд',
  htRoundBody:
    'Когда открывается последняя плитка раунда, всё поле на мгновение видно целиком. Затем плитки переворачиваются и перемешиваются, и начинается новый раунд.',
  htLanguagesTitle: 'Языки',
  htLanguagesBody:
    '{n}. Любое поле решается обиходными словами. Редкое слово тоже считается, если словарь его знает.',
  htKeysTitle: 'Клавиатура',
  htWildTitle: 'Джокеры',
  htWildBody:
    'Иногда вместо буквы появляется джокер. Джокер считается любой буквой, которая складывает допустимое слово. Уже составленное слово не считается.',
  htHideTitle: 'Буквы, которые прячутся',
  htHideBody:
    'На сложных уровнях открытая буква может сама перевернуться обратно и вернуться позже в этом же раунде. Таймер получает за это лишнее деление, красное. Уже взятые буквы обратно не переворачиваются.',
  tutHideBody:
    'На сложных уровнях открытая буква может сама перевернуться обратно и вернуться позже в этом же раунде. Уже взятые буквы обратно не переворачиваются.',
  htSwapTitle: 'Буквы меняются',
  htSwapBody:
    'Иногда между раундами одна буква заменяется другой. Вы увидите, какая буква убрана и какая добавлена.',
  htLevelsTitle: 'Уровни',
  htLevelEasy:
    'Одни и те же двенадцать букв всю игру, так что их можно выучить и держать список слов в голове. Плитки открываются медленно, а полное поле остаётся на виду достаточно долго, чтобы спокойно выбрать буквы.',
  htLevelMedium:
    'Время от времени одна буква меняется, и становится труднее удерживать в голове слова, которые вы собирались составить позже. Меньше времени смотреть и меньше думать.',
  htLevelHard:
    'Слова из трёх букв перестают засчитываться, а буква меняется примерно каждый второй раунд. Поле едва успевает показаться, как уже перемешивается.',
  htLevelInsane:
    'Всё сразу и на полной скорости. Поле перемешивается почти сразу после последнего хода.',
  htTouchTitle: 'Сенсорный экран',
  htTouchBody:
    'Коснитесь открытой плитки, чтобы взять её букву. Коснитесь любой взятой буквы, чтобы вернуть её. Принять и Сбросить находятся под доской.',

  accountTitle: 'Ваш аккаунт',
  menuProfile: 'Мой профиль',
  menuGames: 'Мои игры',
  menuPublicPage: 'Моя публичная страница',
  menuSignedInAs: 'Вы вошли как',
  signIn: 'Войти',
  signOut: 'Выйти',
  signInTitle: 'Войдите или зарегистрируйтесь',
  signInLead: 'Аккаунт хранит ваши игры, на всех устройствах.',
  signInKeepGame: 'Войдите, чтобы сохранить эту игру, и все следующие.',
  continueWithApple: 'Продолжить с Apple',
  continueWithGoogle: 'Продолжить с Google',
  signInOr: 'или',
  emailLabel: 'Эл. почта',
  codeLabel: 'Код',
  emailMeACode: 'Прислать мне код',
  working: 'Минутку…',
  sendNewCode: 'Отправить новый код',
  codeSent: 'Проверьте почту. Код действует 10 минут.',
  notNow: 'Не сейчас',
  badEmail: 'Это не похоже на адрес электронной почты.',
  badCode: 'Код не подошёл. Коды действуют 10 минут и только один раз.',
  serverBusy: 'Не удалось связаться с сервером. Попробуйте через минуту.',
  serverDown: 'Не удалось связаться с сервером.',
  offline: 'Офлайн',
  leaderboardTitle: 'Таблица лидеров',
  leaderboardEmpty: 'В этой таблице пока нет игр. Стань первым.',
  leaderboardWouldBe: 'Где была бы эта игра',
  leaderboardThisGame: 'Эта игра',
  leaderboardRanksHere: 'Где стоит эта игра',
  leaderboardOutside: 'Вне первой пятёрки.',
  tutAccountTitle: 'Сохрани свои результаты',
  tutAccountBody:
    'Аккаунт сохраняет твои игры, помещает результаты в таблицу лидеров и хранит твой профиль.',
  ssoCancelled: 'Вход отменён.',
  ssoExpired: 'Слишком долго. Войдите заново.',
  ssoNoUsername: 'Не удалось создать аккаунт до конца. Попробуйте через минуту.',
  ssoFailed: 'Не получилось. Попробуйте ещё раз или войдите по почте.',
  tabProfile: 'Профиль',
  countryAny: 'Не скажу',
  findCountry: 'Поиск стран',
  usernameLabel: 'Имя пользователя',
  bioLabel: 'О себе',
  countryLabel: 'Страна',
  bioHint: 'Без ссылок. {left}/{max}',
  save: 'Сохранить',
  saving: 'Сохраняем…',
  saved: 'Сохранено.',
  nameTaken: 'Такое имя уже занято.',
  nameTooShort: 'Чуть длиннее, пожалуйста.',
  nameTooLong: 'Слишком длинно.',
  nameBadCharacters: 'Только буквы, цифры, дефисы и подчёркивания.',
  nameBadEdges: 'Должно начинаться и заканчиваться буквой или цифрой.',
  nameMixedScripts: 'Выберите один алфавит и держитесь его.',
  nameReserved: 'Похоже на имя, которое выдаём мы сами. Выберите другое.',
  nameUnusable: 'Это имя использовать нельзя.',
  bioTooLong: 'В поле «О себе» помещается не больше {max} символов.',
  bioHasLink: 'Ссылки в поле «О себе» не разрешены.',
  bioHasControl: 'Здесь есть символы, которых это поле не допускает.',
  bioUnusable: 'Этот текст использовать нельзя.',
  gamesHeading: 'Игры',
  gamesEmpty: 'Здесь пока пусто. Игры, сыгранные после входа, появятся тут.',
  gamesEmptyShort: 'Здесь пока пусто.',
  gamesLoading: 'Читаем ваши игры…',
  theirGamesLoading: 'Читаем их игры…',
  profileLoading: 'Читаем профиль…',
  playerNotFound: 'Здесь нет никого с таким именем.',
  columnWhen: 'Когда',
  columnRounds: 'раунда',
  columnGame: 'Игра',
  gameLoading: 'Читаем игру…',
  gameUnreadable: 'Эту игру не удалось прочитать.',
  gameNotRanked: 'Сыграна по изменённым правилам, поэтому не идёт в зачёт.',
  detailNotKept: 'Слова и доски этой игры больше не хранятся.',
  openingBoard: 'Начальная доска',
  roundByRound: 'Раунд за раундом',
  roundNothing: 'ничего не найдено',
  changeReplaced: '{letter} заменена в ячейке {slot}',
  changeWildOn: '{letter} джокер в ячейке {slot}',
  changeWildOff: '{letter} джокер убран из ячейки {slot}',
  keepThisGame: 'Сохранить эту игру',
  savedToYourGames: 'Сохранено в ваших играх.',

  reportThis: 'Пожаловаться',
  reportHeading: 'Пожаловаться на это',
  reportTheName: 'Имя',
  reportTheBio: 'Описание',
  reportTheScore: 'Очки',
  reportWhy: 'Что с ним не так? Не обязательно.',
  reportSend: 'Отправить жалобу',
  reportSent: 'Спасибо. Кто-нибудь посмотрит.',
  reportSignIn: 'Войдите, чтобы на что-то пожаловаться.',
  reportGone: 'Этого больше нет.',
  reportCancel: 'Отмена',

  deleteAccount: 'Удалить мой аккаунт',
  deleteAccountWhat:
    'Это навсегда удалит твой профиль и все сохранённые игры. Отменить будет нельзя.',
  deleteAccountAsk: 'Пришлите мне код',
  deleteConfirm: 'Удалить навсегда',
  deleteGone: 'Твоего аккаунта больше нет. Спасибо за игру.',
  deleteNoAddress:
    'На этом аккаунте нет подтверждённого адреса, поэтому удаление нельзя проверить. Свяжитесь с нами, и мы сделаем это вручную.',

  rulesetFacts: 'Слова от {letters} · новая плитка каждые {seconds}',
  plurals: {
    words: { one: '{n} слово', few: '{n} слова', many: '{n} слов', other: '{n} слова' },
    rounds: { one: '{n} раунд', few: '{n} раунда', many: '{n} раундов', other: '{n} раунда' },
    flips: { one: '{n} ход', few: '{n} хода', many: '{n} ходов', other: '{n} хода' },
    ticks: { one: '{n} такт', few: '{n} такта', many: '{n} тактов', other: '{n} такта' },
    points: { one: '{n} очко', few: '{n} очка', many: '{n} очков', other: '{n} очка' },
    minLetters: { one: '{n} буквы', few: '{n} букв', many: '{n} букв', other: '{n} буквы' },
    seconds: { one: '{n} секунды', few: '{n} секунды', many: '{n} секунды', other: '{n} секунды' },
  },
}
